/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useEffect, useState, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { BaseDepartment, Department } from "types/models/menu"
import { SortDrawer } from "components/drawers/SortDrawer"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { MenuOverviewRow } from "components/common"
import { sortMenuItemNewLogicWithSortedIndexes } from "utils/sort_helper"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import {
  flattenInfiniteHierarchies,
  useMenuV3HierarchiesInfinite,
} from "queries"
import { mapHierarchyEntitiesToDepartments } from "utils/menuV3Mapper"

const rowHeight = 64

interface SortDepartmentsDrawerProps {
  visible: boolean
  onClose(): void
}

export const SortDepartmentsDrawer: FC<SortDepartmentsDrawerProps> = ({
  visible,
  onClose,
}: SortDepartmentsDrawerProps) => {
  const dispatch = useDispatch()
  const [sortedIndexes, setSortedIndexes] = useState<[number, number][]>([])

  const {
    merchantID,
    overview: { departments: reduxDepartments, section },
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    isUCS,
  } = useSelector((state: AppState) => state.menu)

  const enablePartialMenu = useEnablePartialMenu()

  // Fetch departments from query when partial menu is enabled
  const { currentData: hierarchiesData } = useMenuV3HierarchiesInfinite(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !merchantID,
    },
  )

  // Use query departments when partial menu is enabled, otherwise use Redux departments
  const departments = useMemo(() => {
    if (enablePartialMenu && hierarchiesData) {
      const allEntities = flattenInfiniteHierarchies(hierarchiesData)
      return mapHierarchyEntitiesToDepartments(allEntities || [])
    }
    return reduxDepartments
  }, [enablePartialMenu, hierarchiesData, reduxDepartments])

  useEffect(() => {
    setSortedIndexes([])
  }, [departments])

  const textMap = useTranslationsMap({
    sortCategoriesSuccess: "menu.notifications.sortCategories.successMessage",
  })

  const onSortEnd = (sortedDepartments: Department[]) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewRearrangeDepartment,
      action: EventAction.submitRearrangeDepartment,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SECTION_ID: section?.sectionID ?? "",
      },
    })

    const sorts = isUCS
      ? sortMenuItemNewLogicWithSortedIndexes({
          originalList: departments,
          sortedIndexes,
          sortKey: "sortOrder",
          itemKey: "itemClassID",
        })
      : sortedDepartments.map((department, index) => ({
          resourceID: department.itemClassID ?? "",
          sortOrder: sortedDepartments.length - index,
        }))

    dispatch(
      actionCreator.menu.sortDepartments({
        merchantID,
        sorts,
        successMessage: textMap.sortCategoriesSuccess,
        merchantGroupID,
        menuGroupID,
      }),
    )
  }

  return (
    <SortDrawer
      title={useTranslation("menu.drawers.sort.categories")}
      items={departments}
      visible={visible}
      onClose={() => {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewRearrangeDepartment,
          action: EventAction.clickExit,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            SECTION_ID: section?.sectionID,
          },
        })
        onClose()
      }}
      onSave={onSortEnd}
      itemSize={rowHeight}
      renderRowContent={(department: BaseDepartment) => (
        <MenuOverviewRow
          name={department.itemClassName ?? ""}
          count={department.totalItems ?? 0}
          countNotAvailable={department.totalUnavailableItems}
        />
      )}
      onSort={({ oldIndex, newIndex }) => {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewRearrangeDepartment,
          action: EventAction.rearrangeDepartment,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            SECTION_ID: section?.sectionID ?? "",
          },
        })
        if (isUCS) {
          setSortedIndexes(prevState => [...prevState, [oldIndex, newIndex]])
        }
      }}
      onClear={() => {
        setSortedIndexes([])
      }}
      data-testid="sort-department-drawer"
    />
  )
}
